/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';

import type { InjectedRouter } from 'js/lib/connectToRouter';

import { Button, Grid, PageGridContainer, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import Markdown from 'bundles/cms/components/Markdown';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import type { Maybe } from 'bundles/front-page/__generated__/baseContentfulTypes';
import type {
  LoggedOutHomePageContentFieldsFragment,
  MarketingContentFieldsFragment,
} from 'bundles/front-page/__generated__/contentfulTypes';
import CourseraForBusinessCTA from 'bundles/front-page/components/modules/CourseraForBusinessCTA';
import { handleConsumerCTAClick } from 'bundles/front-page/utils';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const IMAGE_MAX_WIDTH = 606;
const IMAGE_MAX_HEIGHT = 553;

// preserve aspect ratio
const M_WEB_IMAGE_MAX_WIDTH = 303;
const M_WEB_IMAGE_MAX_HEIGHT = 277;

const AUTH_MODE = 'signup';

const styles = {
  promoAdContainer: () => css`
    background-color: #ebf3ff;
    height: 400;
    padding-bottom: 50px;
    position: relative;

    ${breakpoints.down('sm')} {
      display: block;
      height: 100%;
      padding-bottom: 25px;
    }
  `,
  promoImgContainer: () => css`
    margin: -32px 52px -32px 0;
    max-width: 606px;
    width: 100%;
    ${breakpoints.down('sm')} {
      margin: 0 auto;
      padding: 0 12px 0 16px;
      position: relative;
      top: -22px;
      width: auto;
    }

    ${breakpoints.down('xs')} {
      > div {
        margin: var(--cds-spacing-300) auto 0;
        padding-bottom: 0;
        position: unset;
        height: auto;
      }
    }
  `,
  promoAdTextContainer: () => css`
    padding: 0 20px;
    ${breakpoints.down('sm')} {
      display: block;
      margin: 0 auto;
      max-width: 480px;
      width: 100%;
    }
  `,
  headline: () => css`
    margin-bottom: 20px;
    margin-top: 45px;
    ${breakpoints.down('md')} {
      margin-top: var(--cds-spacing-600);
    }
    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-200);
    }
  `,
  adTextParagraph: css`
    font-family: 'Source Sans Pro', Arial, sans-serif;
    font-size: 20px;
    line-height: 28px;
    margin: 0;
    margin-bottom: 32px;
    max-width: 463px;
    width: 100%;

    ${breakpoints.down('xs')} {
      font-size: var(--cds-font-size-200);
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  subtitle: css`
    font-weight: 600;

    &::before {
      content: '-';
      display: inline-block;
      padding-right: 10px;
    }

    ${breakpoints.down('xs')} {
      font-size: var(--cds-font-size-200);
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  ctaContainer: () => css`
    margin-bottom: 0;
    margin-top: 20px;
    width: 100%;
    ${breakpoints.down('sm')} {
      margin-bottom: 20px;
      padding-bottom: 20px;
      max-width: 430px;
      width: auto;
    }

    ${breakpoints.down('xs')} {
      margin-bottom: 0;
      padding-bottom: 0;

      button {
        max-width: unset;
        width: 100%;
        margin-bottom: 0;
      }
    }
  `,
  joinForFree: () => css`
    margin-right: 24px;
    margin-bottom: 24px;
    ${breakpoints.down('xs')} {
      display: block;
      margin-right: 0;
      width: 100%;
    }
  `,
};

type Context = {
  router: InjectedRouter;
};

type Props = {
  content?: Maybe<
    MarketingContentFieldsFragment &
      Pick<LoggedOutHomePageContentFieldsFragment, 'outcomesFootnote' | 'outcomesC4BCtaText'>
  >;
};

const PromoAd: React.FunctionComponent<Props> = ({ content }, { router }: Context) => {
  const isMobileDeviceWidth = useMediaQuery(breakpoints.down('xs'));
  const trackEvent = useTracker();
  const handleOnClick = () => {
    trackEvent('click_button', {
      button: { name: 'signup' },
      pageSection: { sectionName: 'learner_outcomes' },
    });
    handleConsumerCTAClick(router, AUTH_MODE);
  };

  if (!content) {
    return null;
  }

  const { visualAlternativeText, visual, title, description, callToActionLabel, outcomesFootnote, outcomesC4BCtaText } =
    content;

  return (
    <section className="rc-PromoAdRedesign" css={styles.promoAdContainer}>
      <PageGridContainer>
        <Grid container justifyContent="center" alignItems="center" spacing={16}>
          {visual?.url && (
            <Grid item xs={12} md={6} sm={12} css={styles.promoImgContainer}>
              <LazyImgix
                alt={visualAlternativeText ?? ''}
                src={visual.url}
                maxWidth={isMobileDeviceWidth ? M_WEB_IMAGE_MAX_WIDTH : IMAGE_MAX_WIDTH}
                maxHeight={isMobileDeviceWidth ? M_WEB_IMAGE_MAX_HEIGHT : IMAGE_MAX_HEIGHT}
                style={{ marginLeft: 'auto', marginRight: 'auto', width: '100%' }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6} sm={12} css={styles.promoAdTextContainer}>
            <Typography2
              component="h2"
              css={styles.headline}
              variant={isMobileDeviceWidth ? 'titleSmall' : 'titleLarge'}
            >
              {title}
            </Typography2>
            <Markdown
              source={description}
              css={styles.adTextParagraph}
              disallowedTypes={['paragraph']}
              unwrapDisallowed
            />
            {outcomesFootnote && (
              <Typography2 component="span" variant="bodyPrimary" css={styles.subtitle}>
                {outcomesFootnote}
              </Typography2>
            )}
            <div css={styles.ctaContainer}>
              <TrackedButton
                className="join-for-free-btn"
                css={styles.joinForFree}
                trackingName="consumer_cta_double_blue"
                onClick={handleOnClick}
                variant="primary"
                size="medium"
              >
                {callToActionLabel}
              </TrackedButton>
              {outcomesC4BCtaText && <CourseraForBusinessCTA label={outcomesC4BCtaText} />}
            </div>
          </Grid>
        </Grid>
      </PageGridContainer>
    </section>
  );
};

PromoAd.contextTypes = {
  router: PropTypes.object,
};

export default PromoAd;
